<script lang="ts">
  /**
   * Demo-only "simulated classroom screen". Shown INSIDE the student app's
   * stream stage while in demo mode so the kid understands what the teacher /
   * class kiosk sees of their own live actions (visible tile, raise-hand,
   * mic/cam state, a sent message). Purely presentational — it takes props and
   * renders. No network, no LiveKit. Mirrors the real kiosk's visual language
   * (raise-hand banner, mic/cam status pill, text box, status LED).
   */
  import Avatar from "./Avatar.svelte";

  /** Header title, e.g. "Klassen-Ansicht · das sieht die Klasse". */
  export let title: string;
  export let studentName: string = "";
  export let studentImage: string | null = null;
  /** Selected camera label, e.g. "Klassenzimmer". */
  export let cameraName: string = "";
  export let raiseHand: boolean = false;
  export let audioOn: boolean = false;
  export let videoOn: boolean = false;
  /** Pre-formatted hand-up caption, e.g. "Lena meldet sich". */
  export let handUpText: string = "";
  /** A sent chat message; empty = none. */
  export let message: string = "";

  // LED color/state by priority (highest first): raise-hand (amber, blink) >
  // video (cyan) > audio (blue) > connected (green). Mirrors the kiosk's LED
  // intent ordering.
  $: led = raiseHand
    ? { color: "#FFA500", blink: true }
    : videoOn
      ? { color: "#00FFFF", blink: false }
      : audioOn
        ? { color: "#0000FF", blink: false }
        : { color: "#00FF00", blink: false };
</script>

<div class="class-preview" data-testid="demo-class-preview">
  <div class="cp-header">
    <span class="cp-title" data-testid="demo-class-preview-title">{title}</span>
    <span
      class="led"
      data-testid="demo-class-led"
      style="--led:{led.color}"
      class:blink={led.blink}
    ></span>
  </div>

  <div class="cp-stage">
    {#if raiseHand}
      <div class="cp-handbanner" data-testid="demo-class-handbanner">
        {handUpText || "Hand raised"}
      </div>
    {/if}

    <div class="cp-status">
      <span class="cp-status-name">{studentName}</span>
      <span class="cp-icon" class:off={!audioOn} style="color:{audioOn ? '#4ade80' : '#e2e8f0'}">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 2 a3 3 0 0 0-3 3 v6 a3 3 0 0 0 6 0 V5 a3 3 0 0 0-3-3Z" />
          <path d="M19 10 v1 a7 7 0 0 1-14 0 v-1" />
          <path d="M12 18 v4" />
          <path d="M8 22 h8" />
          {#if !audioOn}<path d="M3 3 L21 21" />{/if}
        </svg>
      </span>
      <span class="cp-icon" class:off={!videoOn} style="color:{videoOn ? '#38bdf8' : '#e2e8f0'}">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M2 7 a2 2 0 0 1 2-2 h9 a2 2 0 0 1 2 2 v10 a2 2 0 0 1-2 2 H4 a2 2 0 0 1-2-2Z" />
          <path d="M17 9 l5-3 v12 l-5-3Z" />
          {#if !videoOn}<path d="M3 3 L21 21" />{/if}
        </svg>
      </span>
    </div>

    <div class="cp-tile">
      <Avatar image={studentImage} name={studentName} size={120} />
      {#if studentName}
        <span class="cp-tile-name">{studentName}</span>
      {/if}
      {#if cameraName}
        <span class="cp-camera" data-testid="demo-class-camera">{cameraName}</span>
      {/if}
    </div>

    {#if message}
      <div class="cp-message" data-testid="demo-class-message">
        <span class="cp-message-name">{studentName}</span>
        <span class="cp-message-text">{message}</span>
      </div>
    {/if}
  </div>
</div>

<style>
  .class-preview {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    color: #e2e8f0;
    font-family: inherit;
  }

  .cp-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0 0.25rem;
  }

  .cp-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: #cbd5e1;
    letter-spacing: 0.01em;
  }

  .led {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    flex: 0 0 auto;
    background: var(--led);
    box-shadow: 0 0 8px var(--led);
  }
  .led.blink {
    animation: cp-blink 1s steps(2, end) infinite;
  }
  @keyframes cp-blink {
    50% {
      opacity: 0.2;
    }
  }

  .cp-stage {
    position: relative;
    flex: 1 1 auto;
    min-height: 220px;
    background: #000;
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .cp-handbanner {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    padding: 0.5rem 0.75rem;
    background: #d97706;
    color: #fff;
    font-weight: 700;
    text-align: center;
    font-size: 0.95rem;
    z-index: 3;
  }

  .cp-status {
    position: absolute;
    left: 8px;
    top: 8px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.25rem 0.6rem;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 0.8rem;
  }
  .cp-status-name {
    font-weight: 600;
    white-space: nowrap;
  }
  .cp-icon {
    display: inline-flex;
    align-items: center;
    line-height: 0;
  }
  .cp-icon.off {
    opacity: 0.45;
  }

  .cp-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
    z-index: 1;
  }
  .cp-tile-name {
    font-size: 0.85rem;
    font-weight: 600;
    color: #f8fafc;
  }
  .cp-camera {
    font-size: 0.7rem;
    color: #94a3b8;
    letter-spacing: 0.02em;
  }

  .cp-message {
    position: absolute;
    left: 50%;
    bottom: 10px;
    transform: translateX(-50%);
    z-index: 2;
    max-width: 90%;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding: 0.6rem 0.9rem;
    background: rgba(0, 0, 0, 0.78);
    border-radius: 12px;
    text-align: center;
  }
  .cp-message-name {
    font-size: 0.75rem;
    color: #cbd5e1;
  }
  .cp-message-text {
    font-size: 1.15rem;
    font-weight: 600;
    color: #fff;
    word-break: break-word;
  }
</style>
